<!-- Scrollable list of chat events that auto-scrolls to the newest message and tracks reads. -->

<script lang="ts" setup generic="T extends { seq: number }">
import TransitionList from '@nzyme/vue-transitions/TransitionList.vue';
import { provideContext } from '@nzyme/vue-utils/context.js';
import type { Slots } from '@nzyme/vue-utils/slots.js';
import type { ElementOrVue } from '@nzyme/vue-utils/types.js';
import { unwrapElement } from '@nzyme/vue-utils/unwrapElement.js';
import { useLoadingContext } from '@nzyme/vue-utils/useLoadingContext.js';
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import type { ComponentPublicInstance } from 'vue';

import { ChatEventContainerContext } from './ChatEventContainerContext.js';

/** Distance from the bottom (in px) within which auto-scroll stays active. */
const NEAR_BOTTOM_THRESHOLD = 150;

/** Delay (in ms) after a DOM mutation before triggering smooth scroll. */
const SCROLL_DELAY_MS = 100;

interface ChatContainerProps {
  events: readonly T[];
  /** Scroll to the new messages */
  scrollToNew?: boolean;
  /** Container to scroll to the new messages. Must be stable after mount. */
  scrollContainer?: ElementOrVue | null;
}

interface ChatContainerSlots {
  default: undefined;
}

interface ChatContainerEmits {
  (e: 'event-read', event: T): void;
}

const props = defineProps<ChatContainerProps>();

defineSlots<Slots<ChatContainerSlots>>();
const emit = defineEmits<ChatContainerEmits>();

const loadingCtx = useLoadingContext();

provideContext(ChatEventContainerContext, {
  onVisible: seq => {
    const event = props.events.find(e => e.seq === seq);
    if (event) {
      emit('event-read', event);
    }
  },
});

const element = ref<ComponentPublicInstance>();

/**
 * Whether the user is scrolled near the bottom of the chat container.
 * Written only by the scroll listener here; gates the MutationObserver
 * auto-scroll and is exposed to the parent (read-only) via
 * `v-model:near-bottom` for the new-messages divider logic.
 */
const nearBottom = defineModel<boolean>('nearBottom', { default: true });

let cleanupScrollListener: (() => void) | null = null;
let mutationObserver: MutationObserver | null = null;
let scrollTimeout: ReturnType<typeof setTimeout> | null = null;

/**
 * Highest rendered event seq the auto-scroll has already reacted to. The trigger fires
 * only when a NEW visible message pushes this higher — resolved from the `data-seq`
 * elements actually in the DOM, never from `props.events.length`. This is what keeps
 * patient-invisible lifecycle events (which grow `events` but render nothing) and
 * structural mutations (the busy indicator leaving, the NewMessagesIndicator v-if
 * flipping) from moving the viewport, and stops the view from being yanked up while the
 * user reads a long message that is still the last one.
 */
let lastScrolledSeq = Number.NEGATIVE_INFINITY;

onMounted(() => {
  cleanupScrollListener = setupScrollListener();
  setupMutationObserver();
});

onBeforeUnmount(() => {
  cleanupScrollListener?.();
  mutationObserver?.disconnect();
  if (scrollTimeout) {
    clearTimeout(scrollTimeout);
  }
});

// Initial load — scroll to new messages indicator if present, otherwise to pre-last event
watch(
  () => loadingCtx.root.loadedOnce,
  async loaded => {
    if (!loaded) {
      return;
    }

    await nextTick();
    scrollToInitialPosition('instant');
  },
);

/**
 * Highest `data-seq` currently rendered in the DOM, or undefined if none. Resolving the
 * scroll target from event seqs — not a DOM child index — is robust against events that
 * render nothing (`ChatMessage` returns null), transition elements still leaving
 * (position:absolute for ~300ms), and the NewMessagesIndicator, all of which make the
 * raw child list an unreliable proxy for "the last message".
 */
function getLastRenderedSeq(): number | undefined {
  const el = element.value?.$el as HTMLElement | undefined;
  if (!el) {
    return undefined;
  }

  let max: number | undefined;
  for (const node of el.querySelectorAll('[data-seq]')) {
    const seq = Number.parseInt(node.getAttribute('data-seq') ?? '', 10);
    if (!Number.isNaN(seq) && (max === undefined || seq > max)) {
      max = seq;
    }
  }

  return max;
}

/** The DOM element of the newest rendered (visible) event, resolved by its seq. */
function getLastRenderedEventElement(): Element | undefined {
  const seq = getLastRenderedSeq();
  if (seq === undefined) {
    return undefined;
  }

  const el = element.value?.$el as HTMLElement | undefined;
  return el?.querySelector(`[data-seq="${seq}"]`) ?? undefined;
}

/** Returns the new messages indicator element if present. */
function getNewMessagesIndicator(): Element | undefined {
  const el = element.value?.$el as HTMLElement | undefined;
  return el?.querySelector('[data-new-messages-indicator]') ?? undefined;
}

/** Resolves the scroll container element, falling back to documentElement. */
function getScrollContainer(): Element {
  if (props.scrollContainer) {
    const el = unwrapElement(props.scrollContainer);
    if (el instanceof Element) {
      return el;
    }
  }

  return document.documentElement;
}

/**
 * Sets up a passive scroll listener on the resolved container to track
 * whether the user is near the bottom.
 */
function setupScrollListener(): () => void {
  const container = getScrollContainer();

  const handler = () => {
    nearBottom.value = container.scrollHeight - container.scrollTop - container.clientHeight < NEAR_BOTTOM_THRESHOLD;
  };

  // For documentElement, scroll events fire on window, not the element itself
  const target: EventTarget = container === document.documentElement ? window : container;
  target.addEventListener('scroll', handler, { passive: true });

  return () => {
    target.removeEventListener('scroll', handler);
  };
}

/**
 * Observes childList mutations on the TransitionList element.
 * When children are added and user is near bottom, triggers a debounced smooth scroll.
 */
function setupMutationObserver() {
  const el = element.value?.$el as HTMLElement | undefined;
  if (!el) {
    return;
  }

  lastScrolledSeq = getLastRenderedSeq() ?? Number.NEGATIVE_INFINITY;

  mutationObserver = new MutationObserver(() => {
    // React only when a NEW visible message appears — i.e. the highest rendered seq grew.
    // Invisible lifecycle events (which grow `props.events` but render nothing) and
    // structural mutations (the busy indicator leaving, the NewMessagesIndicator v-if
    // flipping) leave the highest seq unchanged, so they never move the viewport.
    const lastSeq = getLastRenderedSeq();
    if (lastSeq === undefined || lastSeq <= lastScrolledSeq) {
      return;
    }
    lastScrolledSeq = lastSeq;

    if (!props.scrollToNew || !nearBottom.value) {
      return;
    }

    if (!loadingCtx.root.loadedOnce) {
      return;
    }

    // Clear previous pending scroll — only the last one matters
    if (scrollTimeout) {
      clearTimeout(scrollTimeout);
    }

    scrollTimeout = setTimeout(() => {
      scrollToLastEvent('smooth');
    }, SCROLL_DELAY_MS);
  });

  mutationObserver.observe(el, { childList: true });
}

/**
 * Positions the chat for the initial view: scrolls to the new messages
 * indicator if present, otherwise to the pre-last event.
 */
function scrollToInitialPosition(behavior: ScrollBehavior) {
  const indicator = getNewMessagesIndicator();
  if (indicator) {
    indicator.scrollIntoView({ block: 'start', behavior });
  } else {
    scrollToLastEvent(behavior);
  }
}

/**
 * Aligns the top of the newest rendered event with the top of the viewport, so a long
 * last message (e.g. an agent question with options) is read from its start rather than
 * jammed to the bottom. Resolving the target by seq means it is always the newest
 * message — never an early element the raw child index could resolve to.
 */
function scrollToLastEvent(behavior: ScrollBehavior) {
  const target = getLastRenderedEventElement();
  if (target) {
    target.scrollIntoView({ block: 'start', behavior });
  }
}
</script>

<template>
  <TransitionList
    ref="element"
    :class="css.messages"
    :duration="300"
    animation="slide-down"
  >
    <slot />
  </TransitionList>
</template>

<style lang="scss" module="css">
.messages {
  display: flex;
  flex-direction: column;
}
</style>
